import { Box, styled } from '@mui/material';
import { useMemo } from 'react';

import { StyledCheckbox } from '@/components/form/baseInputs/Checkbox/Checkbox';
import { ArrowDownIcon } from '@/components/icons/ArrowDownIcon';
import { ArrowUpIcon } from '@/components/icons/ArrowUpIcon';

import { DefaultDataTableHeader } from './components/DefaultDataTableHeader';
import { DefaultLoadingOverlay } from './components/DefaultLoadingOverlay';
import {
  AlwaysVisiblePaginatedTableFooter,
  DefaultPaginatedTableFooter,
} from './components/DefaultPaginatedTableFooter';
import { DefaultTableEmptyState } from './components/TableEmptyState';
import { ThemedDataGrid } from './components/ThemedDataGrid';
import { useDefaultColumns } from './hooks/internal/useDefaultColumns';
import { useDefaultProps } from './hooks/internal/useDefaultProps';
import { usePageSize } from './hooks/internal/usePageSize';
import { DataTableProps, InternalDataTableProps } from './types';

// Keep the native MUI checkbox footprint for tables with automatic row heights.
const DataTableCheckbox = styled(StyledCheckbox)({ margin: 9 });

function ColumnSortedAscendingIcon({ className }: { className?: string }) {
  return <ArrowUpIcon className={className} size={20} />;
}

function ColumnSortedDescendingIcon({ className }: { className?: string }) {
  return <ArrowDownIcon className={className} size={20} />;
}

export function DataTable(externalProps: DataTableProps) {
  const props = useDefaultProps(externalProps);
  const {
    header,
    alwaysShowPaginationFooter,
    slots: externalSlots,
    initialState: externalInitialState,
    ...dataGridProps
  } = props;

  const columns = useDefaultColumns(props);
  const pageSize = usePageSize(props);

  const pageSizeOptions = useMemo(() => {
    return [pageSize];
  }, [pageSize]);

  const slots = useMemo(() => {
    let footer: NonNullable<InternalDataTableProps['slots']>['footer'] =
      DefaultPaginatedTableFooter;
    if (props.hideFooterPagination) {
      footer = Box;
    } else if (alwaysShowPaginationFooter) {
      footer = AlwaysVisiblePaginatedTableFooter;
    }

    return {
      baseCheckbox: DataTableCheckbox,
      loadingOverlay: DefaultLoadingOverlay,
      noRowsOverlay: DefaultTableEmptyState,
      footer,
      columnSortedAscendingIcon: ColumnSortedAscendingIcon,
      columnSortedDescendingIcon: ColumnSortedDescendingIcon,
      ...externalSlots,
    };
  }, [alwaysShowPaginationFooter, externalSlots, props.hideFooterPagination]);

  const initialState = useMemo(() => {
    return {
      pagination: { paginationModel: { pageSize } },
      ...externalInitialState,
    };
  }, [externalInitialState, pageSize]);

  return (
    <Box data-testid="DataTable">
      {/* Ideally this is a Toolbar slot (left, center, right, etc.), but someone else can add that later if they need to */}
      {header && <DefaultDataTableHeader>{header}</DefaultDataTableHeader>}
      <Box data-testid="DataTable-tableContainer">
        <ThemedDataGrid
          {...dataGridProps}
          slots={slots}
          columns={columns}
          pageSizeOptions={pageSizeOptions}
          initialState={initialState}
          columnHeaderHeight={48}
        />
      </Box>
    </Box>
  );
}
